import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    TextField,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    InputAdornment,
    Chip,
    Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import PaletteIcon from '@mui/icons-material/Palette';

import { commandPaletteAtom } from 'atoms/commandPaletteAtom';
import { recentCalcsAtom } from 'atoms/recentCalcsAtom';
import { sideNavOpenAtom } from 'atoms/sideNavAtom';
import { themeAtom, type ThemeName } from 'atoms/themeAtom';
import { useReselectCalc } from 'utils/hooks/useReselectCalc';
import { NAV_COMMANDS, filterCommands, type CommandItem } from './commands';

// 実行可能コマンド（検索用フィールド + アイコン + 実処理）。
type RunnableCommand = CommandItem & { icon: React.ReactNode; perform: () => void };

const THEME_OPTIONS: { name: ThemeName; label: string }[] = [
    { name: 'gray', label: 'グレー' },
    { name: 'white', label: 'ホワイト' },
    { name: 'black', label: 'ブラック' },
    { name: 'red', label: 'レッド' },
];

// ⌘K / Ctrl+K で開く検索式コマンドパレット。
// 既存アクション（route 遷移 / 直近計算セット再選択 / テーマ・SideNav トグル）の呼び出し口のみで、
// 新規の副作用・API 呼び出しは作らない。直近再選択は CalcSetIndicator と同一の useReselectCalc を共有する。
export const CommandPalette: React.FC = () => {
    const [open, setOpen] = useAtom(commandPaletteAtom);
    const navigate = useNavigate();
    const recent = useAtomValue(recentCalcsAtom);
    const reselect = useReselectCalc();
    const setSideNavOpen = useSetAtom(sideNavOpenAtom);
    const setTheme = useSetAtom(themeAtom);

    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);

    const close = useCallback(() => setOpen(false), [setOpen]);

    const commands = useMemo<RunnableCommand[]>(() => {
        const nav: RunnableCommand[] = NAV_COMMANDS.map((c) => ({
            id: c.id,
            title: c.title,
            group: '画面移動',
            keywords: c.keywords,
            icon: <ArrowForwardIcon fontSize="small" />,
            perform: () => navigate(c.path),
        }));
        const recents: RunnableCommand[] = recent.map((entry, i) => ({
            id: `recent-${entry.tool}-${entry.powertrain}-${i}`,
            title: `${entry.tool.toUpperCase()}: ${entry.label}`,
            group: '直近の計算セット',
            keywords: `${entry.tool} ${entry.powertrain} recent 直近 ${entry.label}`,
            icon: <HistoryIcon fontSize="small" />,
            perform: () => reselect(entry),
        }));
        const toggles: RunnableCommand[] = [
            {
                id: 'toggle-sidenav',
                title: 'サイドナビを開閉',
                group: '表示',
                keywords: 'sidebar nav サイドナビ 開閉 toggle',
                icon: <ViewSidebarIcon fontSize="small" />,
                perform: () => setSideNavOpen((o) => !o),
            },
            ...THEME_OPTIONS.map((t) => ({
                id: `theme-${t.name}`,
                title: `テーマ: ${t.label}`,
                group: '表示',
                keywords: `theme color テーマ ${t.name} ${t.label}`,
                icon: <PaletteIcon fontSize="small" />,
                perform: () => setTheme(t.name),
            })),
        ];
        return [...nav, ...recents, ...toggles];
    }, [navigate, recent, reselect, setSideNavOpen, setTheme]);

    const filtered = useMemo(() => filterCommands(commands, query), [commands, query]);

    // 開くたびに検索/選択位置を初期化する。
    useEffect(() => {
        if (open) {
            setQuery('');
            setActiveIndex(0);
        }
    }, [open]);

    // 絞り込みが変わったら選択位置を先頭へ戻す。
    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    const run = useCallback(
        (cmd?: RunnableCommand) => {
            if (!cmd) return;
            close();
            cmd.perform();
        },
        [close],
    );

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            run(filtered[activeIndex]);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={close}
            fullWidth
            maxWidth="sm"
            // 画面上部寄せ（一般的なコマンドパレットの配置）。
            sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
            PaperProps={{ sx: { mt: 10 } }}
            aria-label="コマンドパレット"
        >
            <Box sx={{ p: 1.5 }}>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    placeholder="コマンド検索 / 画面移動 / 直近の計算セット"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    inputProps={{ 'aria-label': 'コマンド検索' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            <List dense sx={{ maxHeight: 380, overflowY: 'auto', pt: 0 }}>
                {filtered.length === 0 ? (
                    <ListItem>
                        <ListItemText primary="該当するコマンドがありません" />
                    </ListItem>
                ) : (
                    filtered.map((cmd, i) => (
                        <ListItemButton
                            key={cmd.id}
                            selected={i === activeIndex}
                            onMouseEnter={() => setActiveIndex(i)}
                            onClick={() => run(cmd)}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>{cmd.icon}</ListItemIcon>
                            <ListItemText primary={cmd.title} />
                            <Chip label={cmd.group} size="small" variant="outlined" sx={{ ml: 1 }} />
                        </ListItemButton>
                    ))
                )}
            </List>
        </Dialog>
    );
};
